import type { UploadProps } from 'antd'
import { message, Spin, Upload } from 'antd'
import type { FC } from 'react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, Trash } from '@yy/one-icon'
import { FormItem } from '@/bizComponent/AntdForm/DynamicFormItemGroup'
import AntdRules from '@/bizComponent/AntdForm/rules'
import { FileDownload } from '@/containers/Order/Detail/component/ShippingInfo/FileDownload'
import { useUploadImage } from '@/hooks'
import styles from './index.module.less'
import { PlusOutlined } from '@ant-design/icons'
import type { RcFile } from 'antd/lib/upload'
import type { UploadRequestOption } from 'rc-upload/lib/interface'

interface IProps {
  defaultValue?: string
  required?: boolean
  value?: any
  onChange?: any
  config?: {
    fileType?: string[]
    fileSize?: number
  }
}

function getFileSize(size) {
  if (!size) return ''
  const num = 1024.0 //byte
  if (size < num) return size + 'B'
  if (size < Math.pow(num, 2)) return (size / num).toFixed(0) + 'KB' //kb
  if (size < Math.pow(num, 3)) return (size / Math.pow(num, 2)).toFixed(0) + 'MB' //M
  if (size < Math.pow(num, 4)) return (size / Math.pow(num, 3)).toFixed(0) + 'G' //G
  return (size / Math.pow(num, 4)).toFixed(0) + 'T'
}

// kb
const defaultFileSize = 5
const defaultFileType = ['image/*']
const convertMtoByte = (n: number) => n * 1024 * 1024

const { Dragger } = Upload

const UploadComponent: React.FC<IProps> = ({ config, value, onChange, ...args }) => {
  const fileTypes = config?.fileType || defaultFileType
  const fileSizeLimit = config?.fileSize || defaultFileSize

  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  const { doUpload } = useUploadImage({
    returnKey: 'fileUri',
  })

  const props: UploadProps = {
    name: 'file',
    maxCount: 1,
    disabled: !!value,
    showUploadList: false,
    accept: fileTypes?.map((type) => `.${type}`).join(', '),
    beforeUpload: (file: RcFile) => {
      const maxSize = convertMtoByte(fileSizeLimit)

      if (file.size > maxSize) {
        message.error(
          t('provider.invoice_limit', {
            max: getFileSize(maxSize),
          })
        )
        return false
      }

      return true
    },
    customRequest: async ({ file }: UploadRequestOption) => {
      setLoading(true)
      try {
        const fileKey = await doUpload({
          scene: 'INVOICE_FILE',
          file: file,
          fileName: (file as unknown as RcFile)?.name,
        })
        onChange(fileKey)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      } finally {
        setLoading(false)
      }
      return true
    },
  }

  const content = useMemo(() => {
    if (!value) {
      return (
        <>
          <div className={styles.icon}>
            <PlusOutlined />
          </div>
          <div className="ant-upload-text">
            {t('provider.invoice_format', {
              format: fileTypes?.join(','),
            })}
          </div>
        </>
      )
    }

    return (
      <div className={styles.success} onClick={(e) => e.stopPropagation()}>
        <div className={styles.left}>
          <>
            <FileText style={{ marginRight: 14 }} color="#748399" fontSize={28} />
            <FileDownload
              iconProps={{
                style: { fontSize: 26, color: '#748399', position: 'relative', top: 2 },
              }}
              filePath={value}
            />
          </>
        </div>
        <Trash
          color="#415066"
          fontSize={24}
          onClick={(e) => {
            e.stopPropagation()
            onChange('')
          }}
        />
      </div>
    )
  }, [fileTypes, onChange, t, value])

  return (
    <>
      <Spin spinning={loading}>
        <Dragger {...props} {...args}>
          <div className={styles.container}>{content}</div>
        </Dragger>
      </Spin>
    </>
  )
}

export const InvoicePathField: FC<IProps> = (props) => {
  const { defaultValue, required = false } = props

  return (
    <FormItem
      className={styles.formItem}
      name="provider_invoice_path"
      initialValue={defaultValue}
      required={required}
      rules={[
        AntdRules.requiredUpload({
          required,
        }),
      ]}
    >
      <UploadComponent {...props} />
    </FormItem>
  )
}
